import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'
import { SchoolProfileForm } from '@/components/school/SchoolProfileForm'
import { useSchoolPackages } from '@/hooks/useSchoolPackages'
import { useInstructorAvailability, type AvailabilityWindow } from '@/hooks/useInstructorAvailability'
import { useInstructorVehicle } from '@/hooks/useInstructorVehicle'
import { FormField } from '@/components/ui/FormField'
import { PhoneField } from '@/components/ui/PhoneField'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'
import { CarIcon } from '@/components/icons/CarIcon'
import { getPwChecks, pwStrength, pwAllPass, pwStrengthLabel } from '@/lib/password'
import { ONTARIO_CITIES } from '@/constants/ontario-cities'
import { ONTARIO_REGIONS } from '@/constants/ontario-regions'
import { COUNTRY_CODES, parseToE164, splitE164 } from '@/lib/phone'
import type { SupportedCountryCode } from '@/lib/phone'
import type { VehicleTransmission } from '@/hooks/useSchoolVehicles'

// ── Shared style helpers ──────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  font: '500 12.5px Inter, sans-serif',
  color: 'rgba(27,42,74,0.6)',
  marginBottom: 6,
}

function inputStyle(hasError = false): React.CSSProperties {
  return {
    width: '100%',
    padding: '11px 13px',
    border: `1px solid ${hasError ? '#C0522A' : 'rgba(27,42,74,0.18)'}`,
    borderRadius: 9,
    font: '400 14px Inter, sans-serif',
    color: '#1B2A4A',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
  }
}

const errorStyle: React.CSSProperties = {
  marginTop: 5,
  font: '400 12px Inter, sans-serif',
  color: '#C0522A',
}

const sectionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid rgba(27,42,74,0.08)',
  borderRadius: 14,
  padding: '22px 22px',
  marginBottom: 18,
}

const sectionTitleStyle: React.CSSProperties = {
  font: '600 15px Inter, sans-serif',
  color: '#1B2A4A',
  marginBottom: 16,
}

// ── Instructor profile section ────────────────────────────────────────────────

type FieldError = Partial<Record<
  'fullName' | 'phone' | 'licenceNumber' | 'serviceArea' | 'yearsExperience' | 'bio',
  string
>>

const PHOTO_BUCKET = 'employee-photos'

function InstructorProfileSection() {
  const { profile, instructorProfile, refreshProfile, user } = useAuth()

  // Initialize phone: parse stored E.164 back to country + national
  const storedPhone = profile?.phone ?? null
  const parsedPhone = storedPhone ? splitE164(storedPhone) : null

  const [fullName, setFullName]           = useState(profile?.full_name ?? '')
  const [countryCode, setCountryCode]     = useState<SupportedCountryCode>(parsedPhone?.countryCode ?? 'CA')
  const [phoneNational, setPhoneNational] = useState(parsedPhone?.national ?? '')
  const [licenceNumber, setLicenceNumber] = useState(instructorProfile?.licence_number ?? '')
  const [serviceArea, setServiceArea]     = useState(instructorProfile?.service_area ?? '')
  const [yearsExp, setYearsExp]           = useState<string>(
    instructorProfile?.years_experience != null
      ? String(instructorProfile.years_experience)
      : ''
  )
  const [bio, setBio]                     = useState(instructorProfile?.bio ?? '')
  const [address, setAddress]             = useState(instructorProfile?.address ?? '')

  const [fieldErrors, setFieldErrors]     = useState<FieldError>({})
  const [saving, setSaving]               = useState(false)
  const [saveMsg, setSaveMsg]             = useState('')
  const [saveError, setSaveError]         = useState('')

  // Photo — the instructor's own upload, stored under {their own user_id}/{uuid}.ext in
  // the employee-photos bucket ("employee-photos: self write own photo" / "self replace
  // own photo" storage policies, migration 104), written directly onto
  // instructor_profiles.photo_path (no RPC needed — "instructor_profiles: own" is `for all`).
  const [photoUploading, setPhotoUploading] = useState(false)
  const [photoError, setPhotoError]         = useState('')
  const photoUrl = instructorProfile?.photo_path
    ? supabase.storage.from(PHOTO_BUCKET).getPublicUrl(instructorProfile.photo_path).data.publicUrl
    : null

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !user || photoUploading) return
    setPhotoError('')
    setPhotoUploading(true)
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`
      const { error: upErr } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, { contentType: file.type, upsert: false })
      if (upErr) { setPhotoError('Could not upload photo. Please try again.'); return }
      const { error: updErr } = await supabase.from('instructor_profiles').update({ photo_path: path }).eq('user_id', user.id)
      if (updErr) { setPhotoError('Could not save photo. Please try again.'); return }
      await refreshProfile()
    } finally {
      setPhotoUploading(false)
    }
  }

  const BIO_MAX = 1000

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    setSaveMsg(''); setSaveError(''); setFieldErrors({})

    const errors: FieldError = {}

    if (!fullName.trim()) errors.fullName = 'Full name is required'

    const e164 = parseToE164(phoneNational, countryCode)
    if (!e164) errors.phone = 'Enter a valid phone number for the selected country'

    if (!licenceNumber.trim()) errors.licenceNumber = 'Instructor licence number is required'

    if (!serviceArea) errors.serviceArea = 'Please select your primary city'

    const yearsNum = yearsExp === '' ? null : Number(yearsExp)
    if (yearsExp !== '' && (isNaN(yearsNum!) || yearsNum! < 0 || yearsNum! > 60)) {
      errors.yearsExperience = 'Must be between 0 and 60'
    }

    if (bio.trim().length > BIO_MAX) {
      errors.bio = `Bio must be ${BIO_MAX} characters or less`
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    setSaving(true)
    try {
      const { data, error } = await supabase.rpc('update_instructor_profile', {
        p_full_name:        fullName.trim(),
        p_phone:            e164!,
        p_licence_number:   licenceNumber.trim(),
        p_service_area:     serviceArea,
        p_years_experience: yearsNum ?? undefined,
        p_bio:              bio.trim() || undefined,
        p_address:          address.trim() || undefined,
      })

      if (error) {
        setSaveError('Could not save changes. Please try again.')
        return
      }

      const result = data as { ok: boolean; code?: string; field?: string; message?: string }

      if (!result.ok) {
        if (result.code === 'validation_error' && result.field) {
          const fieldMap: Record<string, keyof FieldError> = {
            full_name:        'fullName',
            phone:            'phone',
            licence_number:   'licenceNumber',
            service_area:     'serviceArea',
            years_experience: 'yearsExperience',
            bio:              'bio',
          }
          const fe = fieldMap[result.field]
          if (fe) setFieldErrors({ [fe]: result.message ?? 'Invalid value' })
          else setSaveError(result.message ?? 'Validation failed')
        } else {
          setSaveError(result.message ?? 'Could not save changes. Please try again.')
        }
        return
      }

      await refreshProfile()
      setSaveMsg('Changes saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSave} noValidate>
      {/* ── Profile section ── */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Profile</div>

        {/* Photo — editable by the instructor, flows to the school's Employees view and
            the student's My Instructor page until the instructor replaces it. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
            background: 'rgba(27,42,74,0.06)', border: '1px solid rgba(27,42,74,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {photoUrl
              ? <img src={photoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ font: '600 22px Inter, sans-serif', color: 'rgba(27,42,74,0.3)' }}>
                  {(fullName || profile?.full_name || '?').trim().charAt(0).toUpperCase()}
                </span>}
          </div>
          <div>
            <label className='btn-teal' style={{
              display: 'inline-block', width: 'auto', padding: '9px 16px', cursor: photoUploading ? 'wait' : 'pointer',
              opacity: photoUploading ? 0.6 : 1,
            }}>
              {photoUploading ? 'Uploading…' : photoUrl ? 'Change photo' : 'Upload photo'}
              <input type='file' accept='image/*' onChange={handlePhotoChange} disabled={photoUploading} style={{ display: 'none' }} />
            </label>
            {photoError && <div style={{ ...errorStyle, marginTop: 8 }}>{photoError}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Full name */}
          <div>
            <label style={labelStyle} htmlFor='settings-name'>Full name</label>
            <input
              id='settings-name'
              type='text'
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder='Your name'
              autoComplete='name'
              style={inputStyle(!!fieldErrors.fullName)}
            />
            {fieldErrors.fullName && <div style={errorStyle}>{fieldErrors.fullName}</div>}
          </div>

          {/* Email — read-only, clickable to open the user's mail client */}
          <div>
            <label style={labelStyle}>Email</label>
            {profile?.email ? (
              <a
                href={`mailto:${profile.email}`}
                style={{
                  display: 'block', font: '400 14px Inter, sans-serif', color: '#0F7C6C',
                  padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9,
                  textDecoration: 'none',
                }}
              >
                {profile.email}
              </a>
            ) : (
              <div style={{
                font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)',
                padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9,
              }}>
                —
              </div>
            )}
          </div>

          {/* Phone: country selector + national number, with a tap-to-call shortcut for
              the number currently saved (parseToE164 handles editing-in-progress safely —
              the call link always reflects the last saved value, not an unsaved edit). */}
          <div>
            <label style={labelStyle} htmlFor='settings-phone'>Phone number</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={countryCode}
                onChange={e => setCountryCode(e.target.value as SupportedCountryCode)}
                aria-label='Country code'
                style={{ ...inputStyle(), flex: '0 0 auto', width: 'auto', paddingRight: 32, cursor: 'pointer' }}
              >
                {COUNTRY_CODES.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <input
                id='settings-phone'
                type='tel'
                value={phoneNational}
                onChange={e => setPhoneNational(e.target.value)}
                placeholder='416 555 0100'
                autoComplete='tel-national'
                style={{ ...inputStyle(!!fieldErrors.phone), flex: 1 }}
              />
              {storedPhone && (
                <a
                  href={`tel:${storedPhone}`}
                  title='Call'
                  style={{
                    flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 44, borderRadius: 9, border: '1px solid rgba(27,42,74,0.18)',
                    background: '#fff', color: '#0F7C6C', textDecoration: 'none', font: '600 16px Inter, sans-serif',
                  }}
                >
                  ☎
                </a>
              )}
            </div>
            {fieldErrors.phone && <div style={errorStyle}>{fieldErrors.phone}</div>}
          </div>

          {/* Instructor licence number */}
          <div>
            <label style={labelStyle} htmlFor='settings-licence'>Instructor licence number</label>
            <input
              id='settings-licence'
              type='text'
              value={licenceNumber}
              onChange={e => setLicenceNumber(e.target.value)}
              placeholder='MTO-XXXXXXXX'
              autoComplete='off'
              style={inputStyle(!!fieldErrors.licenceNumber)}
            />
            {fieldErrors.licenceNumber && <div style={errorStyle}>{fieldErrors.licenceNumber}</div>}
          </div>

          {/* Primary location */}
          <div>
            <label style={labelStyle} htmlFor='settings-city'>Primary city</label>
            <select
              id='settings-city'
              value={serviceArea}
              onChange={e => setServiceArea(e.target.value)}
              style={{ ...inputStyle(!!fieldErrors.serviceArea), cursor: 'pointer' }}
            >
              <option value=''>Select a city…</option>
              {ONTARIO_CITIES.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            {fieldErrors.serviceArea && <div style={errorStyle}>{fieldErrors.serviceArea}</div>}
          </div>

          {/* Address — every instructor's own (employee or contractor, school-invited or
              self-signed-up); a school captures this for an employee at invite time, but a
              contractor never had anywhere to enter one until now, and either can correct it
              here afterward. */}
          <div>
            <AddressAutocomplete label='Address' value={address} onChange={setAddress} placeholder='Your home or business address' />
          </div>
        </div>
      </div>

      {/* ── Professional information ── */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Professional information</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Years of experience */}
          <div>
            <label style={labelStyle} htmlFor='settings-years'>Years of experience</label>
            <input
              id='settings-years'
              type='number'
              min={0}
              max={60}
              value={yearsExp}
              onChange={e => setYearsExp(e.target.value)}
              placeholder='e.g. 5'
              style={inputStyle(!!fieldErrors.yearsExperience)}
            />
            {fieldErrors.yearsExperience && <div style={errorStyle}>{fieldErrors.yearsExperience}</div>}
          </div>

          {/* Bio */}
          <div>
            <label style={labelStyle} htmlFor='settings-bio'>
              Bio
              <span style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.35)', marginLeft: 8 }}>
                optional — {BIO_MAX - bio.trim().length} chars remaining
              </span>
            </label>
            <textarea
              id='settings-bio'
              value={bio}
              onChange={e => setBio(e.target.value)}
              rows={4}
              placeholder='Brief description of your teaching style, certifications, or background…'
              style={{
                ...inputStyle(!!fieldErrors.bio),
                resize: 'vertical',
                minHeight: 90,
                fontFamily: 'Inter, sans-serif',
              }}
            />
            {fieldErrors.bio && <div style={errorStyle}>{fieldErrors.bio}</div>}
          </div>
        </div>
      </div>

      {/* Save */}
      {saveMsg   && <div className='banner-success' style={{ marginBottom: 14 }}>{saveMsg}</div>}
      {saveError && <div className='banner-error'   style={{ marginBottom: 14 }}>{saveError}</div>}
      <button
        type='submit'
        className='btn-teal'
        disabled={saving}
        style={{ width: 'auto', padding: '11px 22px', opacity: saving ? 0.65 : 1, marginBottom: 18 }}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  )
}

// ── Instructor vehicle section (migration 106) ───────────────────────────────
// Independent instructors/contractors manage their own vehicle (instructor_vehicles),
// while school employees have a vehicle assigned from the school's fleet (school_vehicles).
// This section shows whichever applies: editable for own vehicle, read-only for assigned.

function InstructorVehicleSection() {
  const { user } = useAuth()
  const { ownVehicle, assignedVehicle, loading, saving, uploadingPhoto, createVehicle, updateVehicle, deleteVehicle, uploadVehiclePhoto, photoUrl } = useInstructorVehicle(user?.id)

  const [editing, setEditing] = useState(false)
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [year, setYear] = useState('')
  const [colour, setColour] = useState('')
  const [transmission, setTransmission] = useState<VehicleTransmission | ''>('')
  const [kmRun, setKmRun] = useState('')
  const [licencePlate, setLicencePlate] = useState('')
  const [vin, setVin] = useState('')
  const [photoPath, setPhotoPath] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [saveMsg, setSaveMsg] = useState('')

  useEffect(() => {
    if (ownVehicle) {
      setMake(ownVehicle.make)
      setModel(ownVehicle.model)
      setYear(ownVehicle.year ? String(ownVehicle.year) : '')
      setColour(ownVehicle.colour ?? '')
      setTransmission(ownVehicle.transmission ?? '')
      setKmRun(ownVehicle.kmRun ? String(ownVehicle.kmRun) : '')
      setLicencePlate(ownVehicle.licencePlate ?? '')
      setVin(ownVehicle.vin)
      setPhotoPath(ownVehicle.photoPath)
    }
  }, [ownVehicle])

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const result = await uploadVehiclePhoto(file)
    if (!result.ok) {
      setError('Could not upload photo. Please try again.')
      return
    }
    setPhotoPath(result.path)
  }

  const handleSave = async () => {
    setError('')
    setSaveMsg('')
    if (!make.trim()) { setError('Make is required.'); return }
    if (!model.trim()) { setError('Model is required.'); return }
    if (!vin.trim()) { setError('VIN is required.'); return }
    if (vin.trim().length !== 17) { setError('VIN must be exactly 17 characters.'); return }

    const params = {
      make: make.trim(),
      model: model.trim(),
      year: year ? Number(year) : null,
      colour: colour.trim() || null,
      kmRun: kmRun ? Number(kmRun) : null,
      licencePlate: licencePlate.trim() || null,
      vin: vin.trim().toUpperCase(),
      transmission: transmission || null,
      photoPath,
    }

    const result = ownVehicle
      ? await updateVehicle(ownVehicle.id, params)
      : await createVehicle(params)

    if (!result.ok) {
      if (result.code === 'vin_invalid') setError('Invalid VIN format. Must be 17 alphanumeric characters (no I, O, or Q).')
      else if (result.code === 'vehicle_already_exists') setError('You already have a vehicle. Delete it first to add a new one.')
      else setError('Could not save vehicle. Please try again.')
      return
    }

    setSaveMsg('Vehicle saved.')
    setEditing(false)
  }

  const handleDelete = async () => {
    if (!ownVehicle) return
    if (!confirm('Delete this vehicle? This cannot be undone.')) return
    const result = await deleteVehicle(ownVehicle.id)
    if (!result.ok) {
      setError('Could not delete vehicle. Please try again.')
      return
    }
    setSaveMsg('Vehicle deleted.')
    setMake('')
    setModel('')
    setYear('')
    setColour('')
    setTransmission('')
    setKmRun('')
    setLicencePlate('')
    setVin('')
    setPhotoPath(null)
    setEditing(false)
  }

  if (loading) return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>

  const vehiclePhotoUrl = photoPath ? photoUrl(photoPath) : assignedVehicle?.photoPath ? photoUrl(assignedVehicle.photoPath) : null

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>My Vehicle</div>

      {assignedVehicle ? (
        // School-assigned vehicle (employee) — read-only
        <>
          <div style={{ font: '400 13px/1.5 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 16 }}>
            Assigned to you by your school — not editable here.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
              background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {vehiclePhotoUrl ? (
                <img src={vehiclePhotoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <CarIcon style={{ width: 32, height: 32, color: 'rgba(27,42,74,0.25)' }} />
              )}
            </div>
            <div>
              <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                {[assignedVehicle.year, assignedVehicle.make, assignedVehicle.model].filter(Boolean).join(' ')}
              </div>
              <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                {[assignedVehicle.colour, assignedVehicle.transmission, assignedVehicle.licencePlate].filter(Boolean).join(' · ') || '—'}
              </div>
              {assignedVehicle.vin && (
                <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 4 }}>
                  VIN: {assignedVehicle.vin}
                </div>
              )}
            </div>
          </div>
        </>
      ) : !ownVehicle && !editing ? (
        // No vehicle yet
        <>
          <div style={{ font: '400 13px/1.5 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 14 }}>
            Add your vehicle so students can recognize it.
          </div>
          <button onClick={() => setEditing(true)} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
            + Add vehicle
          </button>
        </>
      ) : editing || ownVehicle ? (
        // Own vehicle — editable
        <>
          {ownVehicle && !editing ? (
            // Display mode
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                  background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {vehiclePhotoUrl ? (
                    <img src={vehiclePhotoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <CarIcon style={{ width: 32, height: 32, color: 'rgba(27,42,74,0.25)' }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                    {[ownVehicle.year, ownVehicle.make, ownVehicle.model].filter(Boolean).join(' ')}
                  </div>
                  <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 2 }}>
                    {[ownVehicle.colour, ownVehicle.transmission, ownVehicle.licencePlate].filter(Boolean).join(' · ') || '—'}
                  </div>
                  <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 4 }}>
                    VIN: {ownVehicle.vin}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setEditing(true)} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
                  Edit vehicle
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  style={{
                    padding: '9px 18px', background: 'transparent', border: '1px solid rgba(224,102,59,0.4)',
                    borderRadius: 9, font: '500 13px Inter, sans-serif', color: '#E0663B', cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </>
          ) : (
            // Edit mode
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
                  background: 'rgba(27,42,74,0.05)', border: '1px solid rgba(27,42,74,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {vehiclePhotoUrl ? (
                    <img src={vehiclePhotoUrl} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <CarIcon style={{ width: 28, height: 28, color: 'rgba(27,42,74,0.25)' }} />
                  )}
                </div>
                <label style={{
                  display: 'inline-block', padding: '8px 16px', background: '#fff',
                  border: '1px solid rgba(27,42,74,0.15)', borderRadius: 8,
                  font: '600 12.5px Inter, sans-serif', color: '#1B2A4A',
                  cursor: uploadingPhoto ? 'not-allowed' : 'pointer', opacity: uploadingPhoto ? 0.6 : 1,
                }}>
                  {uploadingPhoto ? 'Uploading…' : photoPath ? 'Replace photo' : 'Upload photo'}
                  <input type='file' accept='image/*' onChange={handlePhotoChange} disabled={uploadingPhoto} style={{ display: 'none' }} />
                </label>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className='fg3'>
                  <FormField label='Make' value={make} onChange={e => setMake(e.target.value)} placeholder='Honda' />
                  <FormField label='Model' value={model} onChange={e => setModel(e.target.value)} placeholder='Civic' />
                  <FormField label='Year' type='number' value={year} onChange={e => setYear(e.target.value)} placeholder='2020' />
                </div>
                <div className='fg3'>
                  <FormField label='Colour' value={colour} onChange={e => setColour(e.target.value)} placeholder='Blue' />
                  <div>
                    <label style={labelStyle}>Transmission</label>
                    <select value={transmission} onChange={e => setTransmission(e.target.value as VehicleTransmission | '')} className='field-input'>
                      <option value=''>Not set</option>
                      <option value='automatic'>Automatic</option>
                      <option value='manual'>Manual</option>
                    </select>
                  </div>
                  <FormField label='Km run' type='number' value={kmRun} onChange={e => setKmRun(e.target.value)} placeholder='45000' />
                </div>
                <div className='fg2'>
                  <FormField label='Licence plate' value={licencePlate} onChange={e => setLicencePlate(e.target.value)} placeholder='ABCD123' />
                  <FormField label='VIN (17 characters)' value={vin} onChange={e => setVin(e.target.value)} placeholder='1HGBH41JXMN109186' maxLength={17} />
                </div>
              </div>

              {error && <div className='banner-error' style={{ marginTop: 14 }}>{error}</div>}
              {saveMsg && <div className='banner-success' style={{ marginTop: 14 }}>{saveMsg}</div>}

              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button onClick={handleSave} disabled={saving} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
                  {saving ? 'Saving…' : ownVehicle ? 'Save changes' : 'Add vehicle'}
                </button>
                <button
                  onClick={() => { setEditing(false); setError(''); setSaveMsg('') }}
                  style={{ background: 'none', border: 'none', font: '600 13px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  )
}

// ── Instructor service areas (multi-city, multi-region) ──────────────────────
// Separate from InstructorProfileSection's single "primary city" (instructor_profiles.
// service_area, left untouched) — this is the full set of cities the instructor serves
// (public.instructor_service_areas, migration 065), school-visible for a shared
// instructor just like the rest of their profile.

function InstructorServiceAreasSection() {
  const { user } = useAuth()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loaded,   setLoaded]   = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [saveMsg,  setSaveMsg]  = useState('')
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!user) return
    supabase.from('instructor_service_areas').select('city').eq('instructor_id', user.id)
      .then(({ data }) => { setSelected(new Set((data ?? []).map(r => r.city))); setLoaded(true) })
  }, [user])

  const toggleCity = (city: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(city)) next.delete(city)
      else next.add(city)
      return next
    })
  }

  const handleSave = async () => {
    if (saving || !user) return
    setSaveMsg(''); setSaveError('')
    setSaving(true)
    try {
      // Replace the full set: clear existing rows, then write back exactly what's selected.
      const { error: deleteError } = await supabase
        .from('instructor_service_areas')
        .delete()
        .eq('instructor_id', user.id)
      if (deleteError) { setSaveError('Could not save service areas.'); return }

      if (selected.size > 0) {
        const rows = Array.from(selected).map(city => ({
          instructor_id: user.id,
          region: ONTARIO_REGIONS.find(r => r.cities.includes(city))?.region ?? 'Other',
          city,
        }))
        const { error: upsertError } = await supabase.from('instructor_service_areas').upsert(rows, { onConflict: 'instructor_id,city' })
        if (upsertError) { setSaveError('Could not save service areas.'); return }
      }
      setSaveMsg('Service areas saved.')
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>Service areas</div>
      <div style={{ font: '400 13px/1.5 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 14 }}>
        Select every city you teach in. A school you work with can see this list.
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid rgba(27,42,74,0.12)', borderRadius: 10, padding: 10, marginBottom: 14 }}>
        {ONTARIO_REGIONS.map(({ region, cities }) => (
          <div key={region} style={{ marginBottom: 8 }}>
            <div style={{ font: '600 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 4 }}>{region}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {cities.map(city => {
                const checked = selected.has(city)
                return (
                  <div
                    key={city}
                    onClick={() => toggleCity(city)}
                    style={{
                      cursor: 'pointer', padding: '4px 10px', borderRadius: 8,
                      border: `1px solid ${checked ? '#0F7C6C' : 'rgba(27,42,74,0.15)'}`,
                      background: checked ? 'rgba(15,124,108,0.1)' : '#fff',
                      font: '400 12px Inter, sans-serif', color: checked ? '#0F7C6C' : '#1B2A4A',
                    }}
                  >
                    {city}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      {saveMsg   && <div className='banner-success' style={{ marginBottom: 14 }}>{saveMsg}</div>}
      {saveError && <div className='banner-error'   style={{ marginBottom: 14 }}>{saveError}</div>}
      <button className='btn-teal' onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '11px 22px' }}>
        {saving ? 'Saving…' : 'Save service areas'}
      </button>
    </div>
  )
}

// ── Instructor billing (private-lesson pricing + hourly expense) ─────────────
// public.instructor_private_rates (migration 065) — deliberately has NO school-facing
// RLS policy at all, so this is the only place this data is ever shown. A school paying
// this instructor as a contractor sees their OWN rate (what the school pays per hour,
// on the school's Instructors page) but never this section's content.

function InstructorBillingSection() {
  const { user } = useAuth()
  const [g2Rate,  setG2Rate]  = useState('')
  const [expense, setExpense] = useState('15.00')
  const [loaded,  setLoaded]  = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [saveMsg, setSaveMsg] = useState('')
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!user) return
    supabase.from('instructor_private_rates').select('g2_hourly_rate, hourly_expense').eq('instructor_id', user.id).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setG2Rate(data.g2_hourly_rate != null ? String(data.g2_hourly_rate) : '')
          setExpense(String(data.hourly_expense))
        }
        setLoaded(true)
      })
  }, [user])

  const netRate = (() => {
    const gross = Number(g2Rate)
    const exp = Number(expense)
    if (!g2Rate || isNaN(gross) || isNaN(exp)) return null
    return (gross - exp).toFixed(2)
  })()

  const handleSave = async () => {
    if (saving || !user) return
    setSaveMsg(''); setSaveError('')
    const gross = g2Rate === '' ? null : Number(g2Rate)
    if (g2Rate !== '' && (isNaN(gross!) || gross! <= 0)) { setSaveError('Enter a valid hourly rate.'); return }
    const exp = Number(expense)
    if (isNaN(exp) || exp < 0) { setSaveError('Enter a valid hourly expense.'); return }

    setSaving(true)
    try {
      const { error } = await supabase.from('instructor_private_rates').upsert(
        { instructor_id: user.id, g2_hourly_rate: gross, hourly_expense: exp },
        { onConflict: 'instructor_id' }
      )
      if (error) { setSaveError('Could not save billing settings.'); return }
      setSaveMsg('Billing settings saved.')
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>Billing — private students</div>
      <div style={{ font: '400 13px/1.5 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 14 }}>
        What you charge your own private students, and your estimated cost per hour (fuel, wear and tear, insurance). This is never visible to any school.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className='fg2'>
          <FormField label='G2 lesson rate (per hour, CAD)' value={g2Rate} onChange={e => setG2Rate(e.target.value)} placeholder='e.g. 50.00' type='number' />
          <FormField label='Hourly expense estimate (CAD)' value={expense} onChange={e => setExpense(e.target.value)} placeholder='15.00' type='number' />
        </div>
        {netRate !== null && (
          <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
            Net per hour after expenses: <strong style={{ color: '#0F7C6C' }}>${netRate}</strong>
          </div>
        )}
        {saveMsg   && <div className='banner-success'>{saveMsg}</div>}
        {saveError && <div className='banner-error'>{saveError}</div>}
        <button className='btn-teal' onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '11px 22px' }}>
          {saving ? 'Saving…' : 'Save billing settings'}
        </button>
      </div>
    </div>
  )
}

// ── Generic profile section (school_admin / student) ─────────────────────────

function GenericProfileSection() {
  const { profile, refreshProfile } = useAuth()

  const [fullName,  setFullName]  = useState(profile?.full_name ?? '')
  const [phone,     setPhone]     = useState<string | null>(profile?.phone ?? null)
  const [saving,    setSaving]    = useState(false)
  const [saveMsg,   setSaveMsg]   = useState('')
  const [saveError, setSaveError] = useState('')

  const handleSave = async () => {
    if (saving) return
    setSaveMsg(''); setSaveError('')
    setSaving(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim(), phone })
        .eq('id', profile!.id)
      if (error) { setSaveError('Could not save changes.'); return }
      await refreshProfile()
      setSaveMsg('Changes saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>Profile</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormField label='Full name' value={fullName} onChange={e => setFullName(e.target.value)} placeholder='Your name' />
        <PhoneField value={phone} onChange={setPhone} />
        <div>
          <label style={labelStyle}>Email</label>
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9 }}>
            {profile?.email ?? '—'}
          </div>
        </div>
        {saveMsg   && <div className='banner-success'>{saveMsg}</div>}
        {saveError && <div className='banner-error'>{saveError}</div>}
        <button className='btn-teal' onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '11px 22px' }}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}

// ── Student profile section ───────────────────────────────────────────────────
// Separate from GenericProfileSection (still used for school_admin): a student has its
// own supplemental fields (student_profiles, migration 004/067) — sex, G1 licence, and
// guardian contact, required only while date_of_birth computes to under 18 (enforced for
// real by chk_student_profiles_guardian_contact_if_minor, migration 067 — this client-side
// check is a UX convenience, not the actual guarantee).

function computeIsMinor(dob: string): boolean | null {
  if (!dob) return null
  const birth = new Date(dob)
  if (isNaN(birth.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const monthDiff = now.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) age--
  return age < 18
}

function StudentProfileSection() {
  const { profile, studentProfile, refreshProfile } = useAuth()

  const [fullName, setFullName] = useState(profile?.full_name ?? '')
  const [phone,    setPhone]    = useState<string | null>(profile?.phone ?? null)
  const [dob,      setDob]      = useState(studentProfile?.date_of_birth ?? '')
  const [sex,      setSex]      = useState(studentProfile?.sex ?? '')
  const [g1Licence, setG1Licence] = useState(studentProfile?.g1_licence_number ?? '')
  const [g1Issue,   setG1Issue]   = useState(studentProfile?.g1_issue_date ?? '')
  const [guardianEmail, setGuardianEmail] = useState(studentProfile?.guardian_email ?? '')
  const [guardianPhone, setGuardianPhone] = useState<string | null>(studentProfile?.guardian_phone ?? null)

  const [saving,    setSaving]    = useState(false)
  const [saveMsg,   setSaveMsg]   = useState('')
  const [saveError, setSaveError] = useState('')

  const isMinor = computeIsMinor(dob)

  const handleSave = async () => {
    if (saving) return
    setSaveMsg(''); setSaveError('')

    if (isMinor && (!guardianEmail.trim() || !guardianPhone)) {
      setSaveError('A parent/guardian email and phone are required for a student under 18.')
      return
    }

    setSaving(true)
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim(), phone })
        .eq('id', profile!.id)
      if (profileError) { setSaveError('Could not save changes.'); return }

      const { error: studentError } = await supabase
        .from('student_profiles')
        .update({
          date_of_birth:    dob || null,
          sex:               (sex || null) as 'male' | 'female' | null,
          g1_licence_number: g1Licence.trim() || null,
          g1_issue_date:     g1Issue || null,
          guardian_email:    guardianEmail.trim() || null,
          guardian_phone:    guardianPhone,
        })
        .eq('user_id', profile!.id)
      if (studentError) {
        setSaveError(studentError.message.includes('chk_student_profiles_guardian_contact_if_minor')
          ? 'A parent/guardian email and phone are required for a student under 18.'
          : 'Could not save changes.')
        return
      }

      await refreshProfile()
      setSaveMsg('Changes saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>Profile</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormField label='Full name' value={fullName} onChange={e => setFullName(e.target.value)} placeholder='Your name' />
        <PhoneField value={phone} onChange={setPhone} />
        <div>
          <label style={labelStyle}>Email</label>
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '11px 13px', background: 'rgba(27,42,74,0.04)', borderRadius: 9 }}>
            {profile?.email ?? '—'}
          </div>
        </div>
        <div className='fg2'>
          <div>
            <label style={labelStyle} htmlFor='settings-dob'>Date of birth</label>
            <input id='settings-dob' type='date' value={dob} onChange={e => setDob(e.target.value)} style={inputStyle()} />
          </div>
          <div>
            <label style={labelStyle} htmlFor='settings-sex'>Sex</label>
            <select id='settings-sex' value={sex} onChange={e => setSex(e.target.value as '' | 'male' | 'female')} style={{ ...inputStyle(), cursor: 'pointer' }}>
              <option value=''>Prefer not to set</option>
              <option value='male'>Male</option>
              <option value='female'>Female</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ ...sectionTitleStyle, marginTop: 22 }}>G1 licence</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className='fg2'>
          <FormField label='G1 licence #' value={g1Licence} onChange={e => setG1Licence(e.target.value)} placeholder='R1234-56789' />
          <div>
            <label style={labelStyle} htmlFor='settings-g1issue'>G1 issue date</label>
            <input id='settings-g1issue' type='date' value={g1Issue} onChange={e => setG1Issue(e.target.value)} style={inputStyle()} />
          </div>
        </div>
        {studentProfile?.g1_expiry_date && (
          <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            Expires {studentProfile.g1_expiry_date} — computed automatically from the issue date (5 years), not editable.
          </div>
        )}
      </div>

      {isMinor && (
        <>
          <div style={{ ...sectionTitleStyle, marginTop: 22 }}>Parent / guardian contact</div>
          <div style={{ font: '400 13px/1.5 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 14 }}>
            Required for a student under 18. Your instructor and school can see this, the same as the rest of your profile.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField label='Parent/guardian email' value={guardianEmail} onChange={e => setGuardianEmail(e.target.value)} placeholder='parent@example.com' type='email' />
            <PhoneField label='Parent/guardian phone' value={guardianPhone} onChange={setGuardianPhone} />
          </div>
        </>
      )}

      {saveMsg   && <div className='banner-success' style={{ marginTop: 18 }}>{saveMsg}</div>}
      {saveError && <div className='banner-error'   style={{ marginTop: 18 }}>{saveError}</div>}
      <button className='btn-teal' onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '11px 22px', marginTop: 18 }}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  )
}

// Lets an already-onboarded student redeem a SECOND (or later) invite code — e.g. they
// finished with their first instructor and a school later assigns them a new one, or they
// were invited by a different private instructor entirely. The first-time signup wizard
// (SelectRole.tsx) only ever runs once and calls the same 'accept-invite' Edge Function
// with dateOfBirth/G1 fields alongside the token; those fields are reused here as null —
// accept_invitation()'s own `on conflict (user_id) do nothing` on student_profiles makes
// that safe for a student who already has one, and a brand new
// instructor_student_relationships row is still created for the new instructor/school.
function RedeemInviteSection() {
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const handleRedeem = async () => {
    if (submitting || !code.trim()) return
    setSubmitting(true)
    setMessage(''); setError('')
    try {
      const { data, error: fnError } = await supabase.functions.invoke('accept-invite', {
        body: { rawToken: code.trim() },
      })
      if (fnError) { setError('Could not reach the server. Please try again.'); return }
      if (!data?.ok) {
        const codeStr = data?.code ?? ''
        setError(
          codeStr === 'email_mismatch'    ? 'This invite code was not issued to your email address.' :
          codeStr === 'expired'           ? 'This invite code has expired. Ask for a new one.' :
          codeStr === 'already_used'      ? 'This invite code has already been used.' :
          codeStr === 'not_pending'       ? 'This invite code is no longer valid.' :
          codeStr === 'already_connected' ? 'You are already connected to this instructor.' :
          'Invite code is invalid. Please check the code and try again.'
        )
        return
      }
      setMessage('Invite accepted — your new instructor now appears on your dashboard.')
      setCode('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>Redeem an invite code</div>
      <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 12 }}>
        Already have an account and received a new invite code from a school or instructor? Enter it here —
        you don't need to create another account.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input
          value={code} onChange={e => setCode(e.target.value)}
          placeholder='Invite code' className='field-input' style={{ flex: '1 1 220px' }}
          disabled={submitting}
        />
        <button className='btn-teal' onClick={handleRedeem} disabled={submitting || !code.trim()} style={{ width: 'auto', padding: '9px 18px' }}>
          {submitting ? 'Redeeming…' : 'Redeem'}
        </button>
      </div>
      {message && <div className='banner-success' style={{ marginTop: 12 }}>{message}</div>}
      {error && <div className='banner-error' style={{ marginTop: 12 }}>{error}</div>}
    </div>
  )
}

// Every field the onboarding wizard (OnboardingWizard.tsx) captures is also editable here
// — same SchoolProfileForm, same update_school_profile() save path, so there is exactly
// one implementation and no drift between "new school setup" and "edit later."
function SchoolProfileSection() {
  const { selectedSchoolId, schools, loading } = useSelectedSchool()

  if (loading) return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  if (schools.length === 0) {
    return <div className='banner-error'>You need to be an active owner or admin of a school to edit its profile.</div>
  }

  return (
    <div>
      <SchoolSwitcher />
      {selectedSchoolId && (
        <div style={sectionStyle}>
          <SchoolProfileForm schoolId={selectedSchoolId} variant='settings' />
        </div>
      )}
    </div>
  )
}

// Package definitions a school offers (migration 092) — e.g. "Master Package 2", $730,
// 11 in-car hours. Only in_car_hours is a tracked budget; price/perks are descriptive.
function PackagesSection() {
  const { selectedSchoolId, schools, loading: schoolsLoading } = useSelectedSchool()
  const { packages, loading, saving, createPackage, setPackageActive } = useSchoolPackages(selectedSchoolId)

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [inCarHours, setInCarHours] = useState('')
  const [perksText, setPerksText] = useState('')
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')

  if (schoolsLoading) return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>
  if (schools.length === 0) {
    return <div className='banner-error'>You need to be an active owner or admin of a school to manage its packages.</div>
  }

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setFormError(''); setNotice('')
    const hours = parseFloat(inCarHours)
    if (!name.trim()) { setFormError('Give this package a name.'); return }
    if (!Number.isFinite(hours) || hours <= 0) { setFormError('Enter a valid number of in-car hours.'); return }
    const priceValue = price.trim() ? parseFloat(price) : null
    if (price.trim() && (!Number.isFinite(priceValue) || (priceValue as number) < 0)) { setFormError('Enter a valid price.'); return }
    const perks = perksText.split('\n').map(p => p.trim()).filter(Boolean)
    const result = await createPackage({ name: name.trim(), price: priceValue, inCarHours: hours, perks })
    if (!result.ok) { setFormError('Could not create this package. Please try again.'); return }
    setName(''); setPrice(''); setInCarHours(''); setPerksText('')
    setNotice('Package created.')
    setOpen(false)
  }

  return (
    <div>
      <SchoolSwitcher />

      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 16 : 0 }}>
          <div style={sectionTitleStyle}>Lesson packages</div>
          <button
            onClick={() => setOpen(v => !v)}
            className='btn-teal'
            style={{ width: 'auto', padding: '9px 16px' }}
          >
            {open ? 'Cancel' : '+ New package'}
          </button>
        </div>

        {open && (
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Name</div>
                <input value={name} onChange={e => setName(e.target.value)} placeholder='e.g. Master Package 2' className='field-input' disabled={saving} />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Price (CAD, optional)</div>
                <input type='number' min='0' step='0.01' value={price} onChange={e => setPrice(e.target.value)} className='field-input' disabled={saving} />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>In-car hours</div>
                <input type='number' min='0.5' step='0.5' value={inCarHours} onChange={e => setInCarHours(e.target.value)} className='field-input' disabled={saving} />
              </div>
            </div>
            <div>
              <div style={{ font: '500 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Other perks (one per line, optional)</div>
              <textarea
                value={perksText}
                onChange={e => setPerksText(e.target.value)}
                placeholder={'MTO certificate processing\nPickup & drop-off during lessons\nRoad test booking provided'}
                className='field-input'
                rows={3}
                disabled={saving}
              />
            </div>
            {formError && <div className='banner-error'>{formError}</div>}
            <button className='btn-teal' type='submit' disabled={saving} style={{ width: 'auto', padding: '11px 22px', alignSelf: 'flex-start' }}>
              {saving ? 'Saving…' : 'Create package'}
            </button>
          </form>
        )}
        {!open && notice && <div style={{ marginTop: 12, font: '400 13px Inter, sans-serif', color: '#0F7C6C' }}>{notice}</div>}
      </div>

      {loading && <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '12px 0' }}>Loading…</div>}

      {!loading && packages.length === 0 && (
        <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', padding: '12px 0' }}>
          No packages yet. Create one above so you can assign it to students.
        </div>
      )}

      {!loading && packages.map(p => (
        <div key={p.id} style={{ ...sectionStyle, opacity: p.isActive ? 1 : 0.55 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A' }}>
                {p.name}{!p.isActive && <span style={{ marginLeft: 8, font: '600 11px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>ARCHIVED</span>}
              </div>
              <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 4 }}>
                {p.price != null ? `$${p.price.toFixed(2)} ${p.currency} · ` : ''}{p.inCarHours}h in-car lessons
              </div>
              {p.perks.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>
                  {p.perks.map((perk, i) => <li key={i}>{perk}</li>)}
                </ul>
              )}
            </div>
            <button
              onClick={() => setPackageActive(p.id, !p.isActive)}
              disabled={saving}
              style={{
                padding: '6px 12px', background: 'transparent',
                border: '1px solid rgba(27,42,74,0.15)', borderRadius: 7,
                font: '500 12px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer', flexShrink: 0,
              }}
            >
              {p.isActive ? 'Archive' : 'Reactivate'}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function AvailabilitySection() {
  const {
    windows, exceptions, cutoffHours, loading, saving,
    saveWindows, addException, removeException, setCutoffHours,
  } = useInstructorAvailability()

  const [draft, setDraft] = useState<AvailabilityWindow[] | null>(null)
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')
  const [cutoffDraft, setCutoffDraft] = useState('')
  const [excDate, setExcDate] = useState('')
  const [excIsOff, setExcIsOff] = useState(true)
  const [excStart, setExcStart] = useState('')
  const [excEnd, setExcEnd] = useState('')

  const rows = draft ?? windows

  if (loading) return <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>

  const addRow = (weekday: number) => {
    setDraft([...rows, { weekday, startTime: '09:00', endTime: '17:00' }])
  }
  const updateRow = (idx: number, field: 'startTime' | 'endTime', value: string) => {
    const next = [...rows]
    next[idx] = { ...next[idx], [field]: value }
    setDraft(next)
  }
  const removeRow = (idx: number) => {
    setDraft(rows.filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    setFormError(''); setNotice('')
    for (const w of rows) {
      if (w.endTime <= w.startTime) { setFormError('Each window\'s end time must be after its start time.'); return }
    }
    const result = await saveWindows(rows)
    if (!result.ok) { setFormError('Could not save your availability. Please try again.'); return }
    setDraft(null)
    setNotice('Availability saved.')
  }

  const handleSaveCutoff = async () => {
    const hours = parseInt(cutoffDraft, 10)
    if (!Number.isFinite(hours) || hours < 0) { setFormError('Enter a valid number of hours.'); return }
    await setCutoffHours(hours)
    setCutoffDraft('')
  }

  const handleAddException = async () => {
    setFormError('')
    if (!excDate) { setFormError('Pick a date for the exception.'); return }
    if (!excIsOff && (!excStart || !excEnd || excEnd <= excStart)) {
      setFormError('Enter a valid start/end time for special hours.')
      return
    }
    const result = await addException({ date: excDate, isAvailable: !excIsOff, startTime: excIsOff ? null : excStart, endTime: excIsOff ? null : excEnd })
    if (!result.ok) { setFormError('Could not add this exception. Please try again.'); return }
    setExcDate(''); setExcStart(''); setExcEnd(''); setExcIsOff(true)
  }

  return (
    <div>
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Weekly availability</div>
        <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 16 }}>
          The hours a student can request a lesson with you. A weekday with no windows is unavailable
          that whole day. This never limits lessons you book yourself — only requests students send you.
        </div>

        {WEEKDAY_LABELS.map((label, weekday) => {
          const dayRows = rows.map((w, i) => ({ ...w, idx: i })).filter(w => w.weekday === weekday)
          return (
            <div key={weekday} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ font: '600 13px Inter, sans-serif', color: '#1B2A4A' }}>{label}</div>
                <button
                  onClick={() => addRow(weekday)}
                  style={{ background: 'none', border: 'none', font: '500 12px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
                >
                  + Add window
                </button>
              </div>
              {dayRows.length === 0 && (
                <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Unavailable</div>
              )}
              {dayRows.map(w => (
                <div key={w.idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <input type='time' value={w.startTime} onChange={e => updateRow(w.idx, 'startTime', e.target.value)} className='field-input' style={{ width: 120 }} disabled={saving} />
                  <span style={{ color: 'rgba(27,42,74,0.4)' }}>–</span>
                  <input type='time' value={w.endTime} onChange={e => updateRow(w.idx, 'endTime', e.target.value)} className='field-input' style={{ width: 120 }} disabled={saving} />
                  <button
                    onClick={() => removeRow(w.idx)}
                    style={{ background: 'none', border: 'none', color: '#C0522A', cursor: 'pointer', font: '500 12px Inter, sans-serif' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )
        })}

        {formError && <div className='banner-error' style={{ marginTop: 8 }}>{formError}</div>}
        {notice && !draft && <div style={{ marginTop: 8, font: '400 13px Inter, sans-serif', color: '#0F7C6C' }}>{notice}</div>}
        <button className='btn-teal' onClick={handleSave} disabled={saving} style={{ width: 'auto', padding: '11px 22px', marginTop: 12 }}>
          {saving ? 'Saving…' : 'Save availability'}
        </button>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Late cancellation</div>
        <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 12 }}>
          A cancellation inside this window is flagged as late — it's never blocked, just noted on the record.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type='number' min='0' placeholder={String(cutoffHours)}
            value={cutoffDraft} onChange={e => setCutoffDraft(e.target.value)}
            className='field-input' style={{ width: 100 }} disabled={saving}
          />
          <span style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.6)' }}>hours before the lesson (currently {cutoffHours}h)</span>
          <button className='btn-teal' onClick={handleSaveCutoff} disabled={saving || !cutoffDraft} style={{ width: 'auto', padding: '9px 16px' }}>
            Save
          </button>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Date exceptions</div>
        <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 12 }}>
          A one-off day off, or special hours on a date that overrides your usual weekly schedule.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ font: '500 12px Inter, sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 6 }}>Date</div>
            <input type='date' value={excDate} onChange={e => setExcDate(e.target.value)} className='field-input' disabled={saving} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
            <input type='checkbox' checked={excIsOff} onChange={e => setExcIsOff(e.target.checked)} disabled={saving} />
            Day off
          </label>
          {!excIsOff && (
            <>
              <input type='time' value={excStart} onChange={e => setExcStart(e.target.value)} className='field-input' style={{ width: 120 }} disabled={saving} />
              <span style={{ color: 'rgba(27,42,74,0.4)' }}>–</span>
              <input type='time' value={excEnd} onChange={e => setExcEnd(e.target.value)} className='field-input' style={{ width: 120 }} disabled={saving} />
            </>
          )}
          <button className='btn-teal' onClick={handleAddException} disabled={saving} style={{ width: 'auto', padding: '9px 16px' }}>
            Add
          </button>
        </div>

        {exceptions.length === 0 && (
          <div style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>No exceptions set.</div>
        )}
        {exceptions.map(ex => (
          <div key={ex.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(27,42,74,0.06)' }}>
            <div style={{ font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
              {ex.date} — {ex.isAvailable ? `${ex.startTime}–${ex.endTime}` : 'Day off'}
            </div>
            <button onClick={() => removeException(ex.id)} disabled={saving} style={{ background: 'none', border: 'none', color: '#C0522A', cursor: 'pointer', font: '500 12px Inter, sans-serif' }}>
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Main Settings component ───────────────────────────────────────────────────

export default function Settings() {
  const { profile, user } = useAuth()
  const navigate = useNavigate()
  const isInstructor = profile?.primary_role === 'instructor'
  const isSchoolAdmin = profile?.primary_role === 'school_admin'
  const isStudent    = profile?.primary_role === 'student'
  const isEmailUser  = user?.app_metadata?.provider === 'email'

  // ── Change password ───────────────────────────────
  const [currentPw,  setCurrentPw]  = useState('')
  const [newPw,      setNewPw]      = useState('')
  const [confirmPw,  setConfirmPw]  = useState('')
  const [pwSaving,   setPwSaving]   = useState(false)
  const [pwMsg,      setPwMsg]      = useState('')
  const [pwError,    setPwError]    = useState('')
  const pwChecks = getPwChecks(newPw)
  const score    = pwStrength(pwChecks)
  const { label: strengthLabel, color: strengthColor } = pwStrengthLabel(score)

  // ── Delete account ────────────────────────────────
  const [showDelete,  setShowDelete]  = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [deleting,    setDeleting]    = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const handleChangePassword = async () => {
    if (pwSaving) return
    setPwMsg(''); setPwError('')
    if (!currentPw)                         { setPwError('Enter your current password.'); return }
    if (!newPw)                             { setPwError('Enter a new password.'); return }
    if (!pwAllPass(getPwChecks(newPw)))     { setPwError('Password does not meet all requirements.'); return }
    if (newPw !== confirmPw)                { setPwError('Passwords do not match.'); return }
    if (newPw === currentPw)                { setPwError('New password must be different from your current password.'); return }
    setPwSaving(true)
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: profile!.email!,
        password: currentPw,
      })
      if (signInError) { setPwError('Current password is incorrect.'); return }
      const { error: updateError } = await supabase.auth.updateUser({ password: newPw })
      if (updateError) {
        const msg = updateError.message.toLowerCase()
        if (msg.includes('same') || msg.includes('reuse')) {
          setPwError('New password must be different from your previous passwords.')
        } else {
          setPwError('Could not update password. Please try again.')
        }
        return
      }
      setCurrentPw(''); setNewPw(''); setConfirmPw('')
      setPwMsg('Password updated successfully.')
    } finally {
      setPwSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') { setDeleteError('Type DELETE to confirm.'); return }
    setDeleting(true); setDeleteError('')
    try {
      const { error } = await supabase.functions.invoke('delete-account')
      if (error) { setDeleteError('Could not delete account. Please try again.'); return }
      await supabase.auth.signOut()
      navigate('/login?notice=account_deleted', { replace: true })
    } finally {
      setDeleting(false)
    }
  }

  const tabs: { id: 'profile' | 'vehicle' | 'billing' | 'availability' | 'school' | 'packages' | 'security'; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    ...(isInstructor ? [{ id: 'vehicle' as const, label: 'My Vehicle' }] : []),
    ...(isInstructor ? [{ id: 'billing' as const, label: 'Billing' }] : []),
    ...(isInstructor ? [{ id: 'availability' as const, label: 'Availability' }] : []),
    ...(isSchoolAdmin ? [{ id: 'school' as const, label: 'School profile' }] : []),
    ...(isSchoolAdmin ? [{ id: 'packages' as const, label: 'Packages' }] : []),
    { id: 'security', label: 'Security' },
  ]
  const [tab, setTab] = useState<'profile' | 'vehicle' | 'billing' | 'availability' | 'school' | 'packages' | 'security'>('profile')

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>Settings</div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 20 }}>
        Manage your account details
      </div>

      {/* Tabs — one section visible at a time instead of every section stacked, so the
          page doesn't read as an ever-growing list (billing/service-areas/password/danger
          zone one below another) when most visits only need one of them. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid rgba(27,42,74,0.1)' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 4px', marginRight: 20, background: 'none', border: 'none',
              borderBottom: `2px solid ${tab === t.id ? '#0F7C6C' : 'transparent'}`,
              font: `${tab === t.id ? '600' : '500'} 14px Inter, sans-serif`,
              color: tab === t.id ? '#0F7C6C' : 'rgba(27,42,74,0.5)',
              cursor: 'pointer', marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <>
          {isInstructor ? <InstructorProfileSection /> : isStudent ? <StudentProfileSection /> : <GenericProfileSection />}
          {isInstructor && <InstructorServiceAreasSection />}
          {isStudent && <RedeemInviteSection />}
        </>
      )}

      {tab === 'vehicle' && isInstructor && <InstructorVehicleSection />}

      {tab === 'billing' && isInstructor && <InstructorBillingSection />}

      {tab === 'availability' && isInstructor && <AvailabilitySection />}

      {tab === 'school' && isSchoolAdmin && <SchoolProfileSection />}

      {tab === 'packages' && isSchoolAdmin && <PackagesSection />}

      {tab === 'security' && (
      <>
      {/* Change password (email/password users only) */}
      {isEmailUser && (
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Change password</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField
              label='Current password'
              type='password'
              value={currentPw}
              onChange={e => setCurrentPw(e.target.value)}
              placeholder='Your current password'
              autoComplete='current-password'
            />
            <div>
              <FormField
                label='New password'
                type='password'
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                placeholder='Min 10 characters'
                autoComplete='new-password'
              />
              {newPw.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                    {[1,2,3,4,5].map(i => (
                      <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 2,
                        background: i <= score ? strengthColor : 'rgba(27,42,74,0.1)',
                        transition: 'background 0.2s',
                      }} />
                    ))}
                    <div style={{ font: '500 11px Inter, sans-serif', color: strengthColor, flexShrink: 0, marginLeft: 4 }}>
                      {strengthLabel}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {pwChecks.map(c => (
                      <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                          background: c.pass ? '#0F7C6C' : 'rgba(27,42,74,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          font: '700 8px Inter, sans-serif', color: c.pass ? '#fff' : 'rgba(27,42,74,0.3)',
                        }}>✓</div>
                        <span style={{ font: '400 11.5px Inter, sans-serif', color: c.pass ? '#0F7C6C' : 'rgba(27,42,74,0.45)' }}>
                          {c.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <FormField
              label='Confirm new password'
              type='password'
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              placeholder='Repeat your new password'
              autoComplete='new-password'
            />
            {pwMsg   && <div className='banner-success'>{pwMsg}</div>}
            {pwError && <div className='banner-error'>{pwError}</div>}
            <button className='btn-teal' onClick={handleChangePassword} disabled={pwSaving} style={{ width: 'auto', padding: '11px 22px' }}>
              {pwSaving ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div style={{ background: '#fff', border: '1px solid rgba(224,102,59,0.25)', borderRadius: 14, padding: '22px 22px' }}>
        <div style={{ font: '600 15px Inter, sans-serif', color: '#E0663B', marginBottom: 6 }}>Danger zone</div>
        <div style={{ font: '400 13px/1.6 Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 14 }}>
          Deleting your account is permanent and removes all your data from DriveTrack immediately.
        </div>
        {!showDelete ? (
          <button
            onClick={() => setShowDelete(true)}
            style={{ padding: '10px 18px', background: 'transparent', border: '1px solid rgba(224,102,59,0.4)', borderRadius: 9, font: '500 13.5px Inter, sans-serif', color: '#E0663B', cursor: 'pointer' }}
          >
            Delete my account
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className='banner-error'>
              This will permanently delete your account and all associated data. Type <strong>DELETE</strong> to confirm.
            </div>
            <FormField
              label='Type DELETE to confirm'
              value={deleteInput}
              onChange={e => setDeleteInput(e.target.value)}
              placeholder='DELETE'
            />
            {deleteError && <div className='banner-error'>{deleteError}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                style={{ padding: '10px 18px', background: '#E0663B', border: 'none', borderRadius: 9, font: '500 13.5px Inter, sans-serif', color: '#fff', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}
              >
                {deleting ? 'Deleting…' : 'Confirm delete'}
              </button>
              <button
                onClick={() => { setShowDelete(false); setDeleteInput(''); setDeleteError('') }}
                style={{ padding: '10px 18px', background: 'rgba(27,42,74,0.07)', border: 'none', borderRadius: 9, font: '500 13.5px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  )
}
